'use client'

import useCartStore from '@/app/book/stores/useCartStore'
import clsx from 'clsx'
import { asCurrency } from 'lib/utilities/number'
import { getCompDiscounts } from 'lib/utils/orderUtils'
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { currencySymbols } from '../CurrencySelector/CurrencySelector'
import Header from '../Header/Header'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import { PromoCode } from '../PaymentDetails/partials/PromoCodeInput'
import PromoCodeChip from '../PromoCodeChip/PromoCodeChip'
import {
  getAdminFieldsSum,
  getShipments,
  getShippingFieldsSum,
  groupAndSortByProductLine,
  groupByTraveler,
  travelerByTrackingId,
} from './utils'

const SHOW_FEES_ON_STEP = 3

export interface PaymentOrderSummaryProps {
  /**
   * Current step of the order
   */
  currentStep?: number
  /**
   * The eyebrow heading of the summary
   */
  eyebrow: string
  /**
   * Order data to get summray
   */
  order?: Partial<Order>
  /**
   * Admin: Total cancellations (name to cents)
   */
  adminCancellations?: { [key: string]: number }
  /**
   * Admin: Total additions (name to cents)
   */
  adminAdditions?: { [key: string]: number }
  /**
   * Admin: Fees (in cents)
   */
  adminFees?: number
  /**
   * Admin: Refund fees (in cents)
   */
  adminRefundFees?: number
  /**
   * If collapsible summary should be expanded by default
   */
  defaultExpanded?: boolean
  /**
   * Currency to display
   */
  currency?: Currency
  /**
   * Promo codes with additional discount cents
   */
  promoCodes?: PromoCode[]
  /**
   * Optional className override
   */
  className?: string
  /**
   * Callback to trigger currency change
   */
  onChangeCurrency?: () => void
  /**
   * Optional callback when total is computed
   */
  onComputedTotal?: (total?: string, convertedTotal?: string) => void
  /**
   * Callback when promo code is removed
   */
  onRemoveCode?: (promoCode: PromoCode) => void
  /**
   * Currency conversion callback
   */
  convertPrice?: (amount: number, from: Currency, to: Currency) => number
}

interface PaymentOrderSummaryRow {
  /**
   * label id
   */
  id?: string
  /**
   * Label of the row
   */
  label: string
  /**
   * Value of the row
   */
  value: string
  className?: string
  /**
   * Logical branch to show/hide the row
   */
  valueInCents?: number
}

interface CollapsibleSummaryFieldProps
  extends Pick<PaymentOrderSummaryProps, 'defaultExpanded'> {
  /**
   * Heading of the summary field
   */
  heading: PaymentOrderSummaryRow
  /**
   * Generic collapsible content
   */
  children: ReactNode
}

export interface ShipmentItem {
  /**
   * Shipment (actual) product rate
   */
  rate: PaymentOrderSummaryRow
  /**
   * Shipment product insurance
   */
  insurance: PaymentOrderSummaryRow
  /**
   * Shipment traveler label
   */
  traveler?: PaymentOrderSummaryRow
}

function PaymentOrderSummaryItem({
  label,
  value,
  className,
}: PaymentOrderSummaryRow) {
  return (
    <div
      aria-label={'PaymentSummaryItem'}
      className={clsx('flex items-center justify-between w-full', className)}
    >
      <span className="type-body-8">{label}</span>
      <span className="type-body-8">{value}</span>
    </div>
  )
}

function CollapsibleSummaryField({
  heading,
  children,
  defaultExpanded,
}: CollapsibleSummaryFieldProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || false)

  const isFees = heading.label === 'Fees'

  return (
    <div aria-label={heading.label} className="w-full">
      <div className="flex items-center justify-between w-full">
        <button
          type="button"
          className="flex items-center gap-2"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <strong aria-label={heading.label} className="type-body-7">
            {heading.label}
          </strong>
          <Icon
            icon="chevron-down"
            className={clsx('w-6 h-6 transition duration-200 ease-in', {
              'rotate-180': isExpanded,
            })}
            aria-hidden
          />
        </button>
        <strong className="type-body-7">{heading.value}</strong>
      </div>
      <div
        className={clsx(
          'flex flex-col gap-4 mt-2 max-h-[246px] lg:max-h-[254px]',
          {
            'overflow-y-auto': !isFees,
            hidden: !isExpanded,
          },
        )}
      >
        {children}
      </div>
    </div>
  )
}

export default function PaymentOrderSummary({
  currentStep = 0,
  eyebrow,
  order,
  adminCancellations,
  adminAdditions,
  adminFees,
  adminRefundFees,
  defaultExpanded,
  currency = 'USD',
  promoCodes,
  className,
  onChangeCurrency,
  onComputedTotal,
  convertPrice,
}: PaymentOrderSummaryProps) {
  const [shipments, setShipments] = useState<OrderShipment[]>([])
  const [totalPriceFormatted, setTotalPriceFormatted] = useState('0')
  const [convertedPriceFormatted, setConvertedPriceFormatted] = useState('')

  const selectedProducts = useCartStore((state) => state.selectedProducts)

  useEffect(() => {
    if (order) {
      setShipments(getShipments(order))
    }
  }, [order])

  const isAdmin = useMemo(() => {
    return (
      typeof adminAdditions !== 'undefined' ||
      typeof adminCancellations !== 'undefined' ||
      typeof adminFees !== 'undefined' ||
      typeof adminRefundFees !== 'undefined'
    )
  }, [adminAdditions, adminCancellations, adminFees, adminRefundFees])

  const {
    retail_ship_rate,
    paid_insurance_cents,
    handling_fee_cents,
    fuel_surcharge_fee_cents,
    total_price_cents: shipments_total_price_cents,
    wholesale_discount_cents,
    clearance_fee_cents,
  } = useMemo(() => {
    return !isAdmin
      ? getShippingFieldsSum(
          shipments,
          currentStep < SHOW_FEES_ON_STEP
            ? [
                'paid_insurance_cents',
                'fuel_surcharge_fee_cents',
                'clearance_fee_cents',
                'handling_fee_cents',
              ]
            : [],
        )
      : getAdminFieldsSum([
          adminAdditions,
          adminCancellations,
          adminFees,
          adminRefundFees,
        ])
  }, [
    shipments,
    isAdmin,
    adminAdditions,
    adminCancellations,
    adminFees,
    adminRefundFees,
    currentStep,
  ])

  const leg_level_fees =
    currentStep >= SHOW_FEES_ON_STEP
      ? order?.legs?.reduce(
          (fees, leg) => fees + (leg?.pickup_fee_cents || 0),
          0,
        ) || 0
      : 0
  const total_price_cents = shipments_total_price_cents + leg_level_fees

  const returnShipments = shipments.filter(
    (shipment) => shipment.shipment_leg_name === 'Return',
  )

  // unique travelers across all legs

  const travelers = useMemo(
    () =>
      order?.legs
        ?.map((leg) => leg.travelers || [])
        .flat()
        .filter((traveler, index, self) => {
          return self.findIndex((t) => t.id === traveler.id) === index
        }) || [],
    [order?.legs],
  )
  const shippingItems = shipments
    .filter((shipment) => shipment.shipment_leg_name !== 'Return')
    .map<ShipmentItem>((shipment) => {
      const {
        retail_ship_rate,
        paid_insurance_cents,
        products,
        wholesale_discount_cents,
      } = shipment

      // in rare cases product may be undefined, so removed the ! which
      // would throw an exception leading to an application error,
      // instead return an empty product
      const product = products.find(
        (product): product is OrderProductLabel => product.type === 'label',
      ) || {
        type: 'label',
        id: '',
        display_name: '',
        product_line: { display_name: '' },
      }

      const insurance = products.find(
        (product): product is OrderProductInsurance =>
          product.type === 'insurance',
      ) || {
        type: 'insurance',
        id: '',
        display_name: '',
        coverage_amount_cents: 0,
      }

      const { display_name, product_line } = product

      const returnShipment = returnShipments.find(
        (returnShipment) =>
          (returnShipment.products?.find((product) => product?.type === 'label')
            ?.id || 0) === product.id,
      )

      // Since we're filtering out return (roundtrip) shipment products,
      // we need to reflect the aggregate of shipment retail_ship_rate
      // & paid_insurance_cents
      const rateAggregate = returnShipment
        ? retail_ship_rate +
          wholesale_discount_cents +
          returnShipment.retail_ship_rate +
          returnShipment.wholesale_discount_cents
        : retail_ship_rate + wholesale_discount_cents

      const insuranceAggregate = returnShipment
        ? paid_insurance_cents + returnShipment.paid_insurance_cents
        : paid_insurance_cents

      const traveler = travelerByTrackingId(shipment.tracking_id, order)
      return {
        traveler:
          // we only allow one traveler but this condition was
          // only returning true if there was more than one
          traveler && (travelers || []).length >= 1
            ? {
                label: traveler.name,
                value: '',
              }
            : undefined,
        rate: {
          // Prepare `comma` delimited label for concantenation during group sorting
          // {product_line} #{nth} (display_name)
          id: product.id,
          label: `${product_line.display_name},${display_name}`,
          value: asCurrency(rateAggregate / 100, currency),
        },
        insurance: {
          label: `Insurance up to ${asCurrency(insurance.coverage_amount_cents / 100, currency)}`,
          valueInCents: insuranceAggregate,
          value:
            insuranceAggregate == 0
              ? 'Included'
              : asCurrency(insuranceAggregate / 100, currency),
        },
      }
    })

  const shippingItemsByTraveler = groupByTraveler(
    shippingItems,
    selectedProducts,
  )

  const productLineCounter: Record<string, number> = {}
  const sortedShippingItems = Object.entries(shippingItemsByTraveler).map(
    ([traveler, items]) => ({
      traveler: {
        label: traveler,
        value: '',
      },
      items: groupAndSortByProductLine(items, productLineCounter),
    }),
  )

  const totalPromoCodeDiscountCents = promoCodes?.length
    ? promoCodes.reduce((acc, promo) => acc + (promo.discountCents || 0), 0)
    : 0

  const compDiscount = useMemo(() => getCompDiscounts(promoCodes), [promoCodes])

  useEffect(() => {
    let totalPriceDiscounted =
      (total_price_cents - totalPromoCodeDiscountCents) / 100

    // Hard code 0 for comp discount, because the sidebar doesn't apply pick up fees, so the total is incorect
    if (compDiscount.length > 0) totalPriceDiscounted = 0

    const convertedPrice =
      convertPrice?.(totalPriceDiscounted, currency, 'USD') || 0
    const convertedPriceFormatted =
      currency === 'USD' ? '' : asCurrency(totalPriceDiscounted, 'USD') + ' USD'

    const totalPriceFormatted = total_price_cents
      ? asCurrency(convertedPrice, currency)
      : `${currencySymbols[currency]}—`

    setTotalPriceFormatted(totalPriceFormatted)
    setConvertedPriceFormatted(convertedPriceFormatted)
  }, [
    compDiscount,
    total_price_cents,
    totalPromoCodeDiscountCents,
    currency,
    convertPrice,
  ])

  useEffect(() => {
    onComputedTotal?.(totalPriceFormatted, convertedPriceFormatted)
  }, [onComputedTotal, totalPriceFormatted, convertedPriceFormatted])

  const totalFees = useMemo(() => {
    if (currentStep < SHOW_FEES_ON_STEP) return 0

    return (
      (fuel_surcharge_fee_cents || 0) +
      (clearance_fee_cents || 0) +
      (handling_fee_cents || 0) +
      (leg_level_fees || 0)
    )
  }, [
    currentStep,
    fuel_surcharge_fee_cents,
    clearance_fee_cents,
    handling_fee_cents,
    leg_level_fees,
  ])

  return (
    <div className={clsx('w-full', className)}>
      <div className="bg-white p-4 rounded-lg">
        <Header as="h4" className="type-eyebrow-2 mb-4" overrideClassName>
          {eyebrow}
        </Header>
        <div className="flex flex-col gap-2">
          {adminCancellations ? (
            <CollapsibleSummaryField
              heading={{
                label: 'Total Cancellations',
                value: asCurrency(
                  Object.values(adminCancellations).reduce(
                    (sum, current) => sum + current,
                    0,
                  ) / 100,
                  currency,
                ),
              }}
              defaultExpanded={defaultExpanded}
            >
              {Object.entries(adminCancellations).map(([key, value]) => (
                <div key={key} className="flex flex-col gap-2">
                  <PaymentOrderSummaryItem
                    label={key}
                    value={asCurrency(value / 100, currency)}
                  />
                </div>
              ))}
            </CollapsibleSummaryField>
          ) : null}

          {adminAdditions ? (
            <CollapsibleSummaryField
              heading={{
                label: 'Total Additions',
                value: asCurrency(
                  Object.values(adminAdditions).reduce(
                    (sum, current) => sum + current,
                    0,
                  ) / 100,
                  currency,
                ),
              }}
              defaultExpanded={defaultExpanded}
            >
              {Object.entries(adminAdditions).map(([key, value]) => (
                <div key={key} className="flex flex-col gap-2">
                  <PaymentOrderSummaryItem
                    label={key}
                    value={asCurrency(value / 100, currency)}
                  />
                </div>
              ))}
            </CollapsibleSummaryField>
          ) : null}

          {adminFees ? (
            <PaymentOrderSummaryItem
              label="Fees"
              value={asCurrency(adminFees / 100, currency)}
              className="type-body-7"
            />
          ) : null}

          {adminRefundFees ? (
            <PaymentOrderSummaryItem
              label="Refund Fees"
              value={asCurrency(adminRefundFees / 100, currency)}
              className="type-body-7"
            />
          ) : null}

          {sortedShippingItems.length ? (
            <>
              <CollapsibleSummaryField
                heading={{
                  label: 'Shipping',
                  value: asCurrency(
                    (retail_ship_rate +
                      wholesale_discount_cents +
                      paid_insurance_cents) /
                      100,
                    currency,
                  ),
                }}
                defaultExpanded={defaultExpanded}
              >
                {sortedShippingItems.map((item, index) => (
                  <div key={index} className="flex flex-col gap-2">
                    {item.traveler?.label ? (
                      <PaymentOrderSummaryItem
                        {...item.traveler}
                        className="mt-2 font-bold"
                      />
                    ) : null}
                    {item.items.map((subItem, index) => (
                      <Fragment key={index}>
                        <PaymentOrderSummaryItem {...subItem.rate} />
                        {currentStep >= SHOW_FEES_ON_STEP &&
                        subItem.insurance?.valueInCents ? (
                          <PaymentOrderSummaryItem {...subItem.insurance} />
                        ) : null}
                      </Fragment>
                    ))}
                  </div>
                ))}
              </CollapsibleSummaryField>

              {currentStep >= SHOW_FEES_ON_STEP ? (
                <CollapsibleSummaryField
                  heading={{
                    label: 'Fees',
                    value: asCurrency(totalFees / 100, currency),
                  }}
                  defaultExpanded={defaultExpanded}
                >
                  <div className="flex flex-col gap-2">
                    {currentStep >= SHOW_FEES_ON_STEP ? (
                      <PaymentOrderSummaryItem
                        label="Pickup Fee"
                        value={asCurrency(leg_level_fees / 100, currency)}
                      />
                    ) : null}
                    {handling_fee_cents && currentStep >= SHOW_FEES_ON_STEP ? (
                      <PaymentOrderSummaryItem
                        label="Handling Fee"
                        value={asCurrency(handling_fee_cents / 100, currency)}
                      />
                    ) : null}
                    {fuel_surcharge_fee_cents &&
                    currentStep >= SHOW_FEES_ON_STEP ? (
                      <PaymentOrderSummaryItem
                        label="Fuel Surcharge"
                        value={asCurrency(
                          fuel_surcharge_fee_cents / 100,
                          currency,
                        )}
                      />
                    ) : null}
                    {clearance_fee_cents && currentStep >= SHOW_FEES_ON_STEP ? (
                      <PaymentOrderSummaryItem
                        label="Clearance Fee"
                        value={asCurrency(clearance_fee_cents / 100, currency)}
                      />
                    ) : null}
                  </div>
                </CollapsibleSummaryField>
              ) : null}
            </>
          ) : isAdmin ? null : (
            '—'
          )}

          {!!promoCodes?.length && (
            <div className="flex items-start justify-between w-full pt-[6px]">
              <div className="flex items-start gap-2">
                <strong className="type-body-7">Discount</strong>
                <div className="flex flex-col items-start gap-2 mt-[-6px]">
                  {promoCodes.map((item) => (
                    <PromoCodeChip
                      key={item.promoCode}
                      promoCode={item.promoCode}
                      discountCents={item.discountCents}
                      currency={currency}
                    />
                  ))}
                </div>
              </div>
              <strong className="type-body-7">
                {`-${asCurrency(totalPromoCodeDiscountCents / 100, currency)}`}
              </strong>
            </div>
          )}
        </div>
      </div>
      <div className="bg-white p-4 mt-4 rounded-lg">
        <div className="flex items-center justify-between">
          <strong className="text-[14px] font-semibold">
            <span>Total</span>
            {onChangeCurrency ? (
              <Link
                className="text-[12px] font-semibold text-neutral-700 h-auto visited:text-neutral-700"
                inheritFont
                onClick={(e) => {
                  e.preventDefault()
                  onChangeCurrency?.()
                }}
              >
                {`(${currency})`}
              </Link>
            ) : (
              <span className="p-2 text-[12px] font-semibold text-neutral-700 h-auto">{`(${currency})`}</span>
            )}
          </strong>

          <strong className="text-theme-accessibility text-right">
            <div className="type-body-1">{totalPriceFormatted}</div>
            {currency !== 'USD' ? (
              <div className="type-body-7">{convertedPriceFormatted}*</div>
            ) : null}
          </strong>
        </div>
      </div>
      {currency !== 'USD' ? (
        <div className="mt-2 type-body-10">*Charges will appear in USD</div>
      ) : null}
    </div>
  )
}
